import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { ChevronLeft, Menu } from 'lucide-react'
import { Sidebar } from '@/components/layout/Sidebar'
import { navItems } from '@/components/layout/navItems'
import { AiSidebarPanel } from '@/components/layout/AiSidebarPanel'
import { QuickTour } from '@/components/onboarding/QuickTour'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'

// localStorage key — bump the suffix to re-show the tour after a big update.
const QUICK_TOUR_KEY = 'atsume_quicktour_v1'

// スマホの上部バーに「いまどの画面か」を出す。/camps/:id のような下の階層は
// 親の一覧へ戻れるようにする。
function useMobileTitle(pathname: string) {
  const item = navItems
    .filter((n) => (n.to === '/' ? pathname === '/' : pathname === n.to || pathname.startsWith(`${n.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0]
  return { item, isChild: !!item && item.to !== '/' && pathname !== item.to }
}

export function AppLayout() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { item: currentNav, isChild } = useMobileTitle(pathname)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  // Show the quick tour once, on the first visit.
  const [tourOpen, setTourOpen] = useState(() => !localStorage.getItem(QUICK_TOUR_KEY))

  function closeTour() {
    localStorage.setItem(QUICK_TOUR_KEY, 'done')
    setTourOpen(false)
  }

  return (
    <div className="flex h-dvh bg-bg overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex h-full">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((prev) => !prev)}
          onOpenTour={() => setTourOpen(true)}
        />
      </div>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[260px] p-0 border-r border-border" showCloseButton={false}>
          <SheetTitle className="sr-only">{t('misc.appLayout.navigation')}</SheetTitle>
          <Sidebar
            collapsed={false}
            onToggle={() => setMobileOpen(false)}
            onNavigate={() => setMobileOpen(false)}
            onOpenTour={() => { setMobileOpen(false); setTourOpen(true) }}
          />
        </SheetContent>
      </Sheet>

      <main className="flex-1 min-w-0 overflow-hidden flex flex-col">
        {/* Mobile header */}
        <div className="md:hidden flex items-center gap-2 px-3 h-12 border-b border-border bg-surface shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            aria-label={t('misc.appLayout.menu')}
            className="text-text-2"
          >
            <Menu className="size-5" />
          </Button>
          {isChild && currentNav ? (
            <Link
              to={currentNav.to}
              className="flex min-h-10 min-w-0 items-center gap-1 text-sm font-semibold text-text"
            >
              <ChevronLeft className="size-4 shrink-0 text-text-3" />
              <span className="truncate">{t(currentNav.labelKey)}</span>
            </Link>
          ) : currentNav ? (
            <span className="truncate text-sm font-semibold text-text">{t(currentNav.labelKey)}</span>
          ) : (
            <span className="font-[Outfit,system-ui,sans-serif] text-sm font-black tracking-[0.05em] text-text">
              CAMP
            </span>
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </div>
      </main>
      <AiSidebarPanel />
      <QuickTour open={tourOpen} onClose={closeTour} />
    </div>
  )
}
